import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, Plus, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { ADMIN_NAV } from "@/pages/admin/nav";
import { brandService } from "@/services/brandService";
import { errorMessage } from "@/services/apiClient";
import { fmtDate, fmtMoney } from "@/lib/format";

const ROLE_LABEL = { user: "Cliente", manager: "Gestor", agent: "Agente Plus" };

export default function AdminBrandsPage() {
  const [brands, setBrands] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState("");
  const [clients, setClients] = useState([]);
  const [pool, setPool] = useState([]);
  const [selected, setSelected] = useState([]);
  const [target, setTarget] = useState("");
  const [search, setSearch] = useState("");
  const activeBrands = useMemo(() => brands.filter((b) => b.active), [brands]);

  const load = useCallback(async () => {
    try {
      setBrands(await brandService.list());
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, []);

  const loadPool = useCallback(async () => {
    try {
      setPool(await brandService.unassigned(search));
    } catch (e) {
      setPool([]);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadPool(); }, [loadPool]);

  const expand = async (brand) => {
    if (open === brand.id) return setOpen("");
    setOpen(brand.id);
    try {
      setClients(await brandService.brandClients(brand.id));
    } catch (e) {
      toast.error(errorMessage(e));
      setClients([]);
    }
  };

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await brandService.create(name.trim());
      toast.success("Brand criada");
      setName("");
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (brand) => {
    try {
      await brandService.update(brand.id, { active: !brand.active });
      load();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const remove = async (brand) => {
    try {
      await brandService.remove(brand.id);
      toast.success("Brand removida");
      load();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const assign = async () => {
    if (selected.length === 0) return toast.error("Selecione clientes");
    if (!target) return toast.error("Escolha a Brand de destino");
    try {
      const res = await brandService.assign(target, selected);
      toast.success(`${res.updated} cliente(s) atribuído(s)`);
      setSelected([]);
      load();
      loadPool();
      if (open) {
        setClients(await brandService.brandClients(open));
      }
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const detach = async (userId) => {
    try {
      await brandService.assign("", [userId]);
      toast.success("Cliente devolvido a Meridian");
      setClients(await brandService.brandClients(open));
      load();
      loadPool();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const check = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <AppShell nav={ADMIN_NAV} title="Brands" subtitle="Nome White Label mostrado ao lado do logo no terminal do cliente">
      <div data-testid="admin-brands-page" className="space-y-8">
        <form onSubmit={create} className="flex flex-wrap items-end gap-3 rounded-md border border-[#22252F] bg-[#12141A] p-6">
          <div className="min-w-[240px] flex-1 space-y-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">Nome da Brand</p>
            <input data-testid="brand-name-input" value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="Ex: CFXMarkets" maxLength={40}
                   className="h-11 w-full rounded-md border border-[#22252F] bg-[#0F1116] px-3 text-sm text-white outline-none focus:border-[#2563EB]" />
          </div>
          <button type="submit" disabled={busy} data-testid="create-brand-button"
                  className="inline-flex h-11 items-center gap-2 rounded-md bg-[#2563EB] px-5 text-sm font-semibold text-white transition-colors hover:bg-[#1D4ED8] disabled:opacity-50">
            <Plus className="h-4 w-4" /> Criar Brand
          </button>
        </form>

        <section className="space-y-2">
          {brands.map((b) => {
            const expanded = open === b.id;
            return (
              <div key={b.id} data-testid={`brand-row-${b.slug}`} className="rounded-md border border-[#22252F] bg-[#12141A]">
                <div className="flex flex-wrap items-center gap-3 px-5 py-4">
                  <button onClick={() => expand(b)} data-testid={`brand-toggle-${b.slug}`}
                          className="flex flex-1 items-center gap-3 text-left">
                    {expanded ? <ChevronDown className="h-4 w-4 text-[#64748B]" />
                              : <ChevronRight className="h-4 w-4 text-[#64748B]" />}
                    <span className="font-heading uppercase tracking-[0.2em] text-white">{b.name}</span>
                    <span className="font-mono text-[10px] text-[#64748B]">{b.slug}</span>
                    <span className="inline-flex items-center gap-1 font-mono text-xs text-[#94A3B8]">
                      <Users className="h-3.5 w-3.5" /> {b.clients}
                    </span>
                  </button>
                  <button onClick={() => toggleActive(b)} data-testid={`toggle-brand-${b.slug}`}
                          className={`font-mono text-[10px] uppercase tracking-[0.2em] ${b.active ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                    {b.active ? "ativa" : "inativa"}
                  </button>
                  <button onClick={() => remove(b)} data-testid={`delete-brand-${b.slug}`}
                          className="rounded-md border border-[#22252F] p-2 text-[#94A3B8] transition-colors hover:border-[#EF4444]/50 hover:text-[#EF4444]">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                {expanded ? (
                  <div className="overflow-x-auto border-t border-[#1A1D25] px-5 py-4" data-testid={`brand-clients-${b.slug}`}>
                    {clients.length === 0 ? (
                      <p className="py-3 text-sm text-[#64748B]">Nenhum cliente nesta Brand.</p>
                    ) : (
                      <table className="w-full min-w-[640px] text-left text-sm">
                        <thead className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">
                          <tr>
                            <th className="py-2 pr-6">Cliente</th>
                            <th className="py-2 pr-6">Contacto</th>
                            <th className="py-2 pr-6">Perfil</th>
                            <th className="py-2 pr-6 text-right">Saldo</th>
                            <th className="py-2 pr-6">Registo</th>
                            <th className="py-2 text-right">Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {clients.map((c) => (
                            <tr key={c.id} className="border-t border-[#1A1D25]">
                              <td className="py-3 pr-6">
                                <Link to={`/clients/${c.id}`} data-testid={`brand-client-${c.id}`}
                                      className="text-white transition-colors hover:text-[#60A5FA]">{c.name}</Link>
                              </td>
                              <td className="py-3 pr-6 text-xs text-[#94A3B8]">{c.email || "—"}</td>
                              <td className="py-3 pr-6 font-mono text-[10px] uppercase text-[#64748B]">{ROLE_LABEL[c.role] || c.role}</td>
                              <td className="py-3 pr-6 text-right font-mono text-xs text-white">{fmtMoney(c.balance)}</td>
                              <td className="py-3 pr-6 font-mono text-[10px] text-[#64748B]">{fmtDate(c.created_at)}</td>
                              <td className="py-3 text-right">
                                <button onClick={() => detach(c.id)} data-testid={`detach-client-${c.id}`}
                                        className="rounded-md border border-[#22252F] px-3 py-1.5 text-xs text-[#94A3B8] hover:text-white">
                                  Remover da Brand
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
          {brands.length === 0 ? (
            <p className="rounded-md border border-[#22252F] bg-[#12141A] px-6 py-10 text-center text-sm text-[#64748B]" data-testid="brands-empty">
              Sem Brands criadas. Os clientes usam "Meridian" por omissão.
            </p>
          ) : null}
        </section>

        <section data-testid="brand-pool" className="rounded-md border border-[#22252F] bg-[#12141A] p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-heading text-lg font-light tracking-tight text-white md:text-lg">
              Clientes sem Brand (Meridian)
            </h2>
            <input data-testid="pool-search" value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="Procurar cliente"
                   className="h-11 w-56 rounded-md border border-[#22252F] bg-[#0F1116] px-3 text-sm text-white outline-none focus:border-[#2563EB]" />
          </div>

          <div className="mt-4 max-h-80 overflow-y-auto rounded-md border border-[#1A1D25]">
            <table className="w-full min-w-[560px] text-left text-sm">
              <tbody data-testid="pool-body">
                {pool.map((c) => (
                  <tr key={c.id} className="border-b border-[#1A1D25]">
                    <td className="w-10 px-4 py-3">
                      <input type="checkbox" data-testid={`pool-check-${c.id}`}
                             checked={selected.includes(c.id)} onChange={() => check(c.id)}
                             className="h-4 w-4 accent-[#2563EB]" />
                    </td>
                    <td className="px-2 py-3 text-white">{c.name}</td>
                    <td className="px-2 py-3 text-xs text-[#94A3B8]">{c.email || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono text-[10px] text-[#64748B]">{fmtDate(c.created_at)}</td>
                  </tr>
                ))}
                {pool.length === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-[#64748B]" data-testid="pool-empty">
                    Todos os clientes já têm Brand atribuída.
                  </td></tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className="font-mono text-xs text-[#94A3B8]" data-testid="pool-selected-count">
              {selected.length} selecionado(s)
            </span>
            <select data-testid="assign-brand-select" value={target} onChange={(e) => setTarget(e.target.value)}
                    className="h-11 rounded-md border border-[#22252F] bg-[#0F1116] px-3 text-sm text-white">
              <option value="">Escolher Brand de destino</option>
              {activeBrands.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
            <button onClick={assign} data-testid="assign-brand-button"
                    className="inline-flex h-11 items-center rounded-md bg-[#2563EB] px-5 text-sm font-semibold text-white transition-colors hover:bg-[#1D4ED8]">
              Atribuir Brand
            </button>
          </div>
        </section>
      </div>
    </AppShell>
  );
}
